// Sign in with Apple helpers for "Continue with Apple" on the single-page checkout. Prefill
// only: nothing here talks to an EarthLink API, and authorization.code is never read.

// A fresh value per sign-in attempt for Apple's state and nonce.
export const generateRandomString = () => {
  const bytes = new Uint8Array(16);
  window.crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
};

// Decodes a JWT's payload (the middle, base64url-encoded segment). The id_token's signature is
// not verified - it is only read to prefill a form field the customer can edit, never trusted.
export const decodeJwtPayload = (token) => {
  if (typeof token !== 'string') {return null;}
  const segment = token.split('.')[1];
  if (!segment) {return null;}
  try {
    const base64 = segment.replace(/-/g, '+').replace(/_/g, '/');
    const binary = window.atob(base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '='));
    const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
    return JSON.parse(new TextDecoder().decode(bytes));
  } catch {
    return null;
  }
};

// Turns AppleID.auth.signIn()'s popup response into { firstName, lastName, email }, or null
// when the response must be ignored: missing, from a different attempt (state or nonce does not
// match), or without a readable id_token.
//
// - The email comes from the id_token. It may be a Hide My Email @privaterelay.appleid.com
//   address, which is returned as-is.
// - Apple only sends user.name the first time a customer approves EarthLink. After that the
//   names come back empty and the customer types them.
export const parseAppleAuthResponse = (response, { expectedState, expectedNonce }) => {
  const authorization = response?.authorization;
  if (!authorization || !expectedState || authorization.state !== expectedState) {return null;}

  const payload = decodeJwtPayload(authorization.id_token);
  if (!payload || !expectedNonce || payload.nonce !== expectedNonce) {return null;}

  let user = response.user;
  if (typeof user === 'string') {
    try {
      user = JSON.parse(user);
    } catch {
      user = null;
    }
  }

  return {
    firstName: user?.name?.firstName?.trim() || '',
    lastName: user?.name?.lastName?.trim() || '',
    email: payload.email || '',
  };
};

// Apple's error code for the customer pressing Cancel in the popup.
export const APPLE_USER_CANCELLED = 'user_cancelled_authorize';
